import React from "react"
import ChooseHierarchyChip from "./ChooseHierarchyChip"
import strings from "../../../localization/strings"
import * as punchHelper from "../../../helpers/punchHelper"
import { IHierarchyItem, IJobDocument, IJobPhase, IUIUser } from "../../../models"
import AccountStore from "../../../stores/AccountStore"
import JobsChip from "../../job/JobsChip"
import PhasesChip from "../../phase/PhasesChip"
import autobind from "class-autobind"
import * as punchActions from "../../../actions/PunchActions"
import * as userHelper from "../../../helpers/userHelper"
import * as equipmentHelper from "../../../helpers/equipmentHelper"
import * as jobHelper from "../../../helpers/jobHelper"
import * as expenseHelper from "../../../helpers/expenseHelper"

interface IProps {
    byEquipment?: boolean
    // Shift can override punch permissions and allow punching even if level doesn’t allow.
    // we will not use on Shift from now on
    overridePunchPerm?: boolean
    hideIcon?: boolean
    readonly?: boolean
    // cannot change only hierarchy
    fixedHierarchy?: boolean
    hierarchyChipLabel: string
    fixedJob?: boolean
    jobChipLabel: string
    phaseChipLabel: string
    employee: IUIUser
    punchHierarchyItemId: string
    jobId: string
    phaseId: string
    errorHierarchyChipText?: string
    hieararchyChipMaxWidth?: number
    jobChipMaxWidth?: number
    phaseChipMaxWidth?: number
    //even if hierarchy definition is disabled for punch, if job is enabled, we should allow to select this hierarchy.
    //in this case, if job is null, we should show error as chip error
    //if error, we should prevent action in parent component.
    handleSelectHierarchyJobPhase(hierarchyItemId: string, errorHierarchy: string, job: IJobDocument, phase: IJobPhase, intentionalChanged: boolean): void
    getClickableAccessHierarchies?(): string[]
    preventJobSelection?: boolean

    //we need to select hierarchy based on employee punch permission, not logged in user punch permission (when the user has more punch rights than the admin)
    //it's possible when full timecard, for example, employee->timecard and payroll->timecard
    //timecards page is not full timecard in the case filter by punch level
    isEmployeePermissionPriority?: boolean

    //GlobalPunch Add/Edit
    isGlobalPunch?: boolean
    globalPunchEmployees?: IUIUser[]

    //Employee->Timecard->PunchDrawerTable
    isPunchListTable?: boolean
    useEditablePunchTableDataStore?: boolean //if isPunchListTable is true, dont need to set this value
    hierarchyColWidth?: number
    jobColWidth?: number
    phaseColWidth?: number
    tableColsMarginWidth?: number

    //AddUserDialog, AddAccountUserDialog and UserForm should allow no Job even if punch not allowed at hierarchy definition.
    ignoreNotAllowPunchSetting?: boolean

    //able to search for any job that is available based on the employee punch permission.
    //it means that if they searched and choose a job with a different hierarchy than had already been selected that the selection of the job would also change the previously selected hierarchy.
    //it's available on AddPunch, EditPunch and AddShiftTemplate. let's exclude GlobaAdd/Edit punch
    searchJobByEmpPermission?: boolean
    // If you have long chips name, then you should set it true to wrap the chips in multiple lines
    flexWrap?: boolean
    //it used for web->punch
    getCustomNoAccessErrorStr?(selectedHierarchy: IHierarchyItem): string

    //it used for adding shift dialog
    clientId?: string

    //when add/edit expense, we should use DisableExpense of hierarchy definition.
    byExpense?: boolean
    isPPAExpense?: boolean
}
interface IState {
    hierarchyItemId: string
    errorHierarchyItemId: string
    job: IJobDocument
    phase: IJobPhase
}

export default class ChooseHierarchyJobPhaseChipForPunch extends React.Component<IProps, IState> {
    constructor(props: IProps) {
        super(props)
        this.state = {
            hierarchyItemId: props.punchHierarchyItemId,
            errorHierarchyItemId: null,
            job: null,
            phase: null,
        }
        autobind(this)
    }

    componentDidMount() {
        this.getJob(this.props)
    }

    componentWillReceiveProps(nextProps: IProps) {
        this.state.hierarchyItemId !== nextProps.punchHierarchyItemId &&
            this.setState({ hierarchyItemId: nextProps.punchHierarchyItemId })

        const stateJobId = this.state.job ? this.state.job.Id : null
        const statePhaseId = this.state.phase ? this.state.phase.Id : null
        if (stateJobId !== nextProps.jobId || statePhaseId !== nextProps.phaseId) {
            this.getJob(nextProps)
        }

        if (nextProps.byExpense) {
            nextProps.isPPAExpense !== this.props.isPPAExpense &&
                this.handleSelectHierarchyJobPhase(this.state.hierarchyItemId, this.state.job, this.state.phase, nextProps.isPPAExpense, true)
        }
    }

    getJob(props: IProps) {
        props.jobId ?
            props.jobId === AccountStore.DiffJobId ?
                this.setState({
                    job: AccountStore.DiffJob,
                    phase: props.phaseId === AccountStore.DiffPhaseId ? AccountStore.DiffPhase : null
                }) :
                punchActions.getJob(props.jobId, this.props.isPunchListTable || this.props.useEditablePunchTableDataStore)
                    .then((job: IJobDocument) =>
                        this.setState({
                            job,
                            phase: props.phaseId ?
                                props.phaseId === AccountStore.DiffPhaseId ?
                                    AccountStore.DiffPhase :
                                    job.Phases.find(phase => phase.Id === props.phaseId) :
                                null
                        }, () =>
                            this.handleSelectHierarchyJobPhase(job.HierarchyId, job, this.state.phase, props.isPPAExpense, false)
                        )
                    ) :
            this.setState({
                job: null,
                phase: null
            }, () =>
                this.handleSelectHierarchyJobPhase(props.punchHierarchyItemId, null, null, props.isPPAExpense, false)
            )
    }

    handleSelectHierarchyJobPhase(hierarchyItemId: string, job: IJobDocument, phase: IJobPhase, isPPAExpense, intentionalChanged: boolean) {
        const messageForDisablePunches = this.props.byExpense ?
            !isPPAExpense &&
                expenseHelper.getDisableExpenseByDefinition(hierarchyItemId, job ? job.Id : null, phase ? phase.Id : null) ?
                strings.Expense_is_not_allowed_at_this_location :
                null :
            this.props.ignoreNotAllowPunchSetting || this.props.overridePunchPerm ?
                null :
                punchHelper.getDisablePuncheMessageByDefinition(hierarchyItemId, job ? job.Id : null, phase ? phase.Id : null)
        this.setState({ hierarchyItemId, job, phase, errorHierarchyItemId: messageForDisablePunches }, () =>
            this.props.handleSelectHierarchyJobPhase(hierarchyItemId, messageForDisablePunches, job, phase, intentionalChanged)
        )
    }

    getCustomNoAccessErrorStr(selectedHierarchy: IHierarchyItem): string {
        if (this.props.getCustomNoAccessErrorStr)
            return this.props.getCustomNoAccessErrorStr(selectedHierarchy)

        if (this.props.overridePunchPerm)
            return null
        const selectedHierarchyId = selectedHierarchy && selectedHierarchy.Id ? selectedHierarchy.Id : null
        const alloedJobForHierarchy = jobHelper.getAllowJobByHierarchy(selectedHierarchyId)
        if (!alloedJobForHierarchy) {
            const messageForDisablePunches = punchHelper.getDisablePuncheMessageByDefinition(selectedHierarchyId, null, null)
            if (messageForDisablePunches)
                return messageForDisablePunches
        }

        if (this.props.isGlobalPunch) {
            const globalPunchEmployeesAccessHierarchyIds = userHelper.getGlobalPunchEmployeesAccessHierarchyIds(this.props.globalPunchEmployees)
            return globalPunchEmployeesAccessHierarchyIds.includes(selectedHierarchyId) ?
                null :
                strings.One_or_more_of_the_selected_employees_do_not_have_permission_to_punch_to_this_hierarchy
        }
        else {
            const constraintHierarchyIds = this.props.employee ?
                userHelper.getEmployeeAccessHierarchyIds(this.props.employee) :
                equipmentHelper.getEquipmentAccessHierarchyIds()
            return constraintHierarchyIds.includes(selectedHierarchyId) ?
                null :
                strings.You_cant_choose_this_hierarchy_item
        }
    }

    render() {
        return (
            <div style={{ display: "flex", flexWrap: this.props.flexWrap ? "wrap" : "nowrap" }}>
                <div style={{ flex: !this.props.isPunchListTable && 1, display: "flex", width: this.props.hierarchyColWidth, marginLeft: this.props.tableColsMarginWidth, marginRight: this.props.tableColsMarginWidth }}>
                    {!this.props.hideIcon &&
                        <i className="fa fa-map-marker" style={{ marginRight: 10 }} />
                    }
                    <ChooseHierarchyChip
                        chipMaxWidth={this.props.hieararchyChipMaxWidth}
                        readonly={this.props.readonly || this.props.fixedHierarchy}
                        chipLabel={this.props.hierarchyChipLabel}
                        hierarchyItemId={this.state.hierarchyItemId}
                        errorChipText={this.props.errorHierarchyChipText || this.state.errorHierarchyItemId}
                        employee={this.props.employee}
                        handleSelectHierarchy={(hierarchyItemId, intentionalChanged) => this.handleSelectHierarchyJobPhase(hierarchyItemId, null, null, this.props.isPPAExpense, intentionalChanged)}
                        getCustomNoAccessErrorStr={this.getCustomNoAccessErrorStr}
                        getClickableAccessHierarchies={() =>
                            this.props.getClickableAccessHierarchies ?
                                this.props.getClickableAccessHierarchies() :
                                this.props.isGlobalPunch ?
                                    userHelper.getSupervisorClickableHierarchyIdsWithGlobalPunchEmployeesAccess(this.props.globalPunchEmployees) :
                                    this.props.byEquipment && !this.props.employee ?
                                        equipmentHelper.getEquipmentAccessHierarchyIds() :
                                        this.props.isEmployeePermissionPriority ?
                                            userHelper.getEmployeeClickableHierarchyIds(this.props.employee) :
                                            userHelper.getSupervisorClickableHierarchyIdsWithEmployeeAccess(this.props.employee)
                        }
                        isGlobalPunch={this.props.isGlobalPunch}
                        globalPunchEmployees={this.props.globalPunchEmployees}
                        isPunchListTable={this.props.isPunchListTable}
                        hierarchyColWidth={this.props.hierarchyColWidth}
                    />
                </div>
                {AccountStore.getJobsEnabled() && [
                    <div style={{ flex: !this.props.isPunchListTable && 1, width: this.props.jobColWidth, marginLeft: this.props.tableColsMarginWidth, marginRight: this.props.tableColsMarginWidth }} key="job">
                        {!this.props.preventJobSelection &&
                            <JobsChip
                                maxWidth={this.props.jobChipMaxWidth}
                                disabled={this.props.readonly || this.props.fixedJob}
                                label={this.props.jobChipLabel}
                                filterHierarchyId={this.props.searchJobByEmpPermission && this.props.employee ?
                                    this.props.employee.DefaultHierarchyItemId :
                                    this.state.hierarchyItemId
                                }
                                selectedJobs={this.state.job ? [this.state.job] : []}
                                restrictHierarchy={this.props.searchJobByEmpPermission ? false : true}
                                handleSave={jobs => {
                                    const job = jobs.length ? jobs[0] : null
                                    const jobId = job ? job.Id : null
                                    this.handleSelectHierarchyJobPhase(job ? job.HierarchyId : this.state.hierarchyItemId, job, null, this.props.isPPAExpense, this.props.jobId !== jobId)
                                }}
                                isPunchListTable={this.props.isPunchListTable}
                                jobColWidth={this.props.jobColWidth}
                                clientId={this.props.clientId}
                                punchEmployeeId={this.props.employee ? this.props.employee.Id : null}
                            />
                        }
                    </div>,
                    AccountStore.getPhasesEnabled() &&
                    <div style={{ flex: !this.props.isPunchListTable && 1, width: this.props.phaseColWidth, marginLeft: this.props.tableColsMarginWidth, marginRight: this.props.tableColsMarginWidth }} key="phase">
                        {this.state.job && this.state.job.Phases && this.state.job.Phases.filter(i => !i.Deleted).length > 0 &&
                            <PhasesChip
                                maxWidth={this.props.phaseChipMaxWidth}
                                disabled={this.props.readonly}
                                label={this.props.phaseChipLabel}
                                phases={this.state.job.Phases}
                                selectedPhase={this.state.phase}
                                handleSave={phase => this.handleSelectHierarchyJobPhase(this.state.hierarchyItemId, this.state.job, phase, this.props.isPPAExpense, this.props.phaseId !== (phase ? phase.Id : null))}
                                isPunchListTable={this.props.isPunchListTable}
                                phaseColWidth={this.props.phaseColWidth}
                            />
                        }
                    </div>
                ]}
            </div >
        )
    }
}